import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  Paper,
  Button,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { useServiceMapping } from '../../hooks/service/useServiceMapping';
import { useResources } from '../../hooks/resource/useResources';
import ResourceSelect from '../bookings/ResourceSelect';
import { ServiceResourceMapping } from '../../types/service';
import Notifications from '../common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';

interface ResourceMappingSectionProps {
  serviceId: number;
  existingMappings: ServiceResourceMapping[];
  onMappingsChange?: () => void;
}

const ResourceMappingSection: React.FC<ResourceMappingSectionProps> = ({
  serviceId,
  existingMappings,
  onMappingsChange,
}) => {
  const theme = useTheme();
  const [selectedResources, setSelectedResources] = useState<string | null>(null);
  const { createMapping, deleteMapping, isLoading, error } = useServiceMapping();
  const { resources, refetch } = useResources();
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  // Filter out already mapped resources
  const mappedResourceIds = existingMappings.map(m => m.resources.id.toString());
  const availableResources = resources.filter(r => !mappedResourceIds.includes(r.id.toString()));

  const handleAddResource = async () => {
    if (!selectedResources) return;

    console.log('Adding resources:', selectedResources);

    try {
      await createMapping(serviceId, Number(selectedResources));

      setNotification({
        open: true,
        message: 'Staff members mapped successfully',
        severity: SEVERITY.SUCCESS,
      });

      setSelectedResources(null);
      onMappingsChange?.();
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to map staff members',
        severity: SEVERITY.ERROR,
      });
    }
  };

  const handleRemoveResource = async (mappingId: number) => {
    try {
      await deleteMapping(mappingId);
      setNotification({
        open: true,
        message: 'Staff member mapping removed successfully',
        severity: SEVERITY.SUCCESS,
      });
      await refetch();
      onMappingsChange?.();
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to remove staff member mapping',
        severity: SEVERITY.ERROR,
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Add Staff Members
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <ResourceSelect
              selectedResources={selectedResources}
              onChange={setSelectedResources}
              availableResources={availableResources}
            />
          </Box>
          <Button
            variant="contained"
            disableElevation
            startIcon={<Plus size={16} />}
            onClick={handleAddResource}
            disabled={isLoading || !selectedResources}
            sx={{
              height: 40,
              minWidth: 120,
            }}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Mapped Staff Members
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {existingMappings.map(({ id, resources }) => (
            <Chip
              key={id}
              label={resources.name}
              onDelete={() => handleRemoveResource(id)}
              disabled={isLoading}
              sx={{
                backgroundColor: `${theme.palette.primary.main}15`,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: `${theme.palette.primary.main}25`,
                },
              }}
            />
          ))}
          {existingMappings.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No staff members mapped yet
            </Typography>
          )}
        </Box>
      </Paper>

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ResourceMappingSection;
